import Lottie from 'lottie-react';
import * as React from 'react';

import buddyHoverAnimation from '@/assets/animations/buddyHover.json';
import { cn } from '@/lib/utils';

/**
 * Chat Input Component with send button functionality
 */
export const BhanzuBuddyChatInput: React.FC<{
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
}> = ({
  onSendMessage,
  isLoading = false,
  placeholder = 'Type here or talk...',
}) => {
  const [message, setMessage] = React.useState('');

  const handleSendMessage = React.useCallback(() => {
    if (message.trim() && !isLoading) {
      onSendMessage(message.trim());
      setMessage('');
    }
  }, [message, isLoading, onSendMessage]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSendMessage();
    },
    [handleSendMessage]
  );

  return (
    <div className="border-t border-gray-700/50 p-3 sm:p-4">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* BhanzuBuddy Avatar */}
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-magical-moonlight-d3 border border-magical-moonlight-d2 flex items-center justify-center flex-shrink-0">
            <Lottie
              animationData={buddyHoverAnimation}
              loop={true}
              autoplay={true}
              className="w-4 h-4 sm:w-6 sm:h-6"
            />
          </div>

          {/* Input Field with integrated buttons */}
          <div className="flex-1 relative">
            <div className="relative bg-magical-moonlight-d2 border border-gray-600 focus-within:border-bhanzu-orange rounded-xl px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-1 sm:gap-2 transition-colors">
              <input
                type="text"
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={isLoading}
                className={cn(
                  'flex-1 bg-transparent text-white placeholder-gray-400 outline-none',
                  'text-xs sm:text-sm',
                  isLoading && 'opacity-50'
                )}
              />

              {/* Voice Button */}
              <button
                type="button"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                aria-label="Voice input"
                disabled={isLoading}
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              </button>

              {/* Send Button - appears when there's input - Exact Figma MCP design */}
              {message.trim() && (
                <button
                  type="submit"
                  disabled={isLoading || !message.trim()}
                  className={cn(
                    'w-10 h-10 rounded-lg bg-blue-chaos flex items-center justify-center transition-colors',
                    'hover:bg-blue-chaos/80 focus:outline-none focus:ring-2 focus:ring-blue-chaos/20',
                    isLoading && 'opacity-50 cursor-not-allowed'
                  )}
                  aria-label="Send message"
                >
                  <svg
                    className="w-[26.667px] h-[26.667px]"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M7.4 6.32L15.89 3.49C19.7 2.22 21.77 4.3 20.51 8.11L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.92 14.08L7.4 13.24C1.69 11.34 1.69 8.23 7.4 6.32Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M10.11 13.65L13.69 10.06"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
